// External libraries
import React from 'react';
import { useStoreState } from '@/store';

// Internal modules
import { features } from '@/lib';
import { isNextGenSubscription, isSepaMandateCountry } from '@/lib/globals';
import {
  LazyRightOfWithdrawal,
  LazySepaMandate,
  LazyThirdPartyDataConsent,
  LazyUniqueInformationProcessing,
} from '../Components';
import { MarketingOptIn } from '../MarketingOptIn';
import { BillingTerms } from '../../../BillingTerms';
import { DisclosuresContainer } from '../styledComponents';
import { StreamlineOnboardTerms } from '../../../OnboardTerms/StreamlineOnboardTerms';
import type { AppMode } from '@/types';
import type { BillingType } from '@/generated/graphql';
import { CRSTaxDisclosure } from '../CRSTaxDisclosure';

type StreamlineLegalDisclosuresProps = {
  changeHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isPasswordless?: boolean;
  type: AppMode;
};

const StreamlineLegalDisclosures = ({
  changeHandler,
  type,
  isPasswordless,
}: StreamlineLegalDisclosuresProps): JSX.Element => {
  const fiType = useStoreState(({ ui }) => ui.fiType);
  const billingType = features.get('billingType');
  //uses same determination logic for now but will not in the future
  const isBillingAgreement = !!billingType;
  const isNextGenSub = isNextGenSubscription();
  const hasMarketingOptInCheckbox = features.isActive(
    'hasMarketingOptInCheckbox',
  );
  const hasSepaMandate = isSepaMandateCountry();
  const showSepaIfGuest =
    type === 'guest' && hasSepaMandate && fiType === 'bank';
  // Right of Withdraw checkbox is enabled just for countries
  // in TREATMENT_2 of FEATURE_SIGNUP_RIGHT_OF_WITHDRAW
  const shouldRenderRightOfWithdrawalCheckbox = features.get(
    'signupRightOfWithdrawalCheckbox',
  );

  const shouldShowCRSTaxCollectionSection = features.isActive(
    'shouldShowCRSTaxCollectionSection',
  );

  return (
    <DisclosuresContainer data-testid="streamline-disclosure-container">
      <StreamlineOnboardTerms
        changeHandler={changeHandler}
        isPasswordless={isPasswordless}
        type={type}
      />
      {showSepaIfGuest && <LazySepaMandate changeHandler={changeHandler} />}
      {type === 'signup' && (
        <>
          {hasSepaMandate && fiType === 'bank' && (
            <LazySepaMandate changeHandler={changeHandler} />
          )}

          {shouldRenderRightOfWithdrawalCheckbox && (
            <LazyRightOfWithdrawal changeHandler={changeHandler} />
          )}

          {features.isActive('hasThirdPartyDataConsentCheckbox') && (
            <LazyThirdPartyDataConsent changeHandler={changeHandler} />
          )}

          {features.isActive('hasUniqueInformationProcessingCheckbox') && (
            <LazyUniqueInformationProcessing changeHandler={changeHandler} />
          )}

          {shouldShowCRSTaxCollectionSection && <CRSTaxDisclosure />}

          {hasMarketingOptInCheckbox && (
            <MarketingOptIn changeHandler={changeHandler} />
          )}

          {isBillingAgreement && <BillingTerms billingType={billingType} />}
        </>
      )}
      {type === 'guest' && isNextGenSub && (
        <BillingTerms billingType={billingType as BillingType} />
      )}
    </DisclosuresContainer>
  );
};

export { StreamlineLegalDisclosures };
